import React, { useState } from 'react';
import { IconChev, IconGrip, IconTrash } from './Icons.jsx';

export function EndpointsSidebar({
  endpoints, activeId, setActiveId,
  onDeleteEndpoint, onReorderEndpoints,
  collapsed, setCollapsed,
}) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch (_) {}
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overId !== id) setOverId(id);
  };
  const onDrop = (id) => (e) => {
    e.preventDefault();
    if (dragId && dragId !== id) {
      const from = endpoints.findIndex(ep => ep.id === dragId);
      const to = endpoints.findIndex(ep => ep.id === id);
      if (from !== -1 && to !== -1) {
        const next = endpoints.slice();
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onReorderEndpoints(next);
      }
    }
    setDragId(null);
    setOverId(null);
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };

  return (
    <aside style={{
      width: collapsed ? 64 : 260, flexShrink: 0,
      background: '#fff', borderRight: '1px solid var(--border-default)',
      display: 'flex', flexDirection: 'column',
      transition: 'width 200ms var(--ease-out)',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '14px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {!collapsed && (
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Endpoints · {endpoints.length}
          </div>
        )}
        <button className="btn btn-ghost icon-only btn-sm" onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Expand' : 'Collapse'}>
          <IconChev size={12} style={{ transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 200ms' }} />
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '0 8px' }}>
        {endpoints.length === 0 && !collapsed && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--fg-3)' }}>
            No endpoints yet. Click <strong>+ New endpoint</strong> in the header to create one.
          </div>
        )}
        {endpoints.map(ep => (
          <EndpointRow
            key={ep.id}
            endpoint={ep}
            active={ep.id === activeId}
            onClick={() => setActiveId(ep.id)}
            onDelete={() => onDeleteEndpoint(ep.id)}
            collapsed={collapsed}
            count={ep.eventCount ?? 0}
            draggable={!collapsed}
            isDragging={dragId === ep.id}
            isDropTarget={overId === ep.id && dragId && dragId !== ep.id}
            onDragStart={onDragStart(ep.id)}
            onDragOver={onDragOver(ep.id)}
            onDrop={onDrop(ep.id)}
            onDragEnd={onDragEnd}
          />
        ))}
      </div>
    </aside>
  );
}

function EndpointRow({ endpoint, active, onClick, onDelete, collapsed, count,
                      draggable, isDragging, isDropTarget,
                      onDragStart, onDragOver, onDrop, onDragEnd }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: collapsed ? 8 : '9px 10px', borderRadius: 8,
        background: active ? 'var(--yuno-purple-50)' : (hover ? 'var(--neutral-25)' : 'transparent'),
        cursor: 'pointer', marginBottom: 2,
        border: active ? '1px solid var(--yuno-purple-100)' : '1px solid transparent',
        boxShadow: isDropTarget ? 'inset 0 2px 0 var(--yuno-purple-600)' : 'none',
        opacity: isDragging ? 0.4 : 1,
        transition: 'background 120ms var(--ease-out), opacity 120ms',
        justifyContent: collapsed ? 'center' : 'flex-start',
        position: 'relative',
      }}>
      {!collapsed && hover && (
        <span style={{ position: 'absolute', left: -2, color: 'var(--fg-4)', cursor: 'grab', display: 'inline-flex' }} title="Drag to reorder">
          <IconGrip size={12} />
        </span>
      )}
      <span style={{
        width: 26, height: 26, borderRadius: 7, flexShrink: 0,
        background: endpoint.color, color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, letterSpacing: '-0.04em',
        backgroundImage: endpoint.image ? `url(${endpoint.image})` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
        overflow: 'hidden',
      }}>{!endpoint.image && endpoint.glyph}</span>
      {!collapsed && (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 500,
              color: active ? 'var(--yuno-purple-700)' : 'var(--fg-1)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{endpoint.name}</div>
            <div style={{
              fontSize: 11, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>/{endpoint.id}</div>
          </div>
          <div style={{
            fontSize: 11, fontWeight: 500,
            color: active ? 'var(--yuno-purple-700)' : 'var(--fg-3)',
            background: active ? '#fff' : 'var(--neutral-100)',
            padding: '2px 7px', borderRadius: 999, minWidth: 22, textAlign: 'center',
          }}>{count}</div>
          {hover && (
            <button
              className="btn btn-ghost icon-only btn-sm"
              onClick={(e) => { e.stopPropagation(); if (confirm(`Delete ${endpoint.name} endpoint and all its events?`)) onDelete(); }}
              style={{ position: 'absolute', right: 36, background: '#fff' }}
              title="Delete endpoint"
            ><IconTrash size={12} /></button>
          )}
        </>
      )}
    </div>
  );
}
